import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  TbLeaf,
  TbUsers,
  TbTrendingUp,
  TbWorld,
  TbTarget,
  TbAward,
  TbCalendar,
  TbBook,
  TbMail,
  TbPhone,
  TbMapPin,
  TbChevronRight,
  TbStar,
  TbBuilding,
  TbHandClick,
  TbBulb,
  TbShield,
  TbFileText,
  TbMessage,
  TbArrowRight,
  TbPlayerPlay,
  TbChartBar,
  TbBuildingFactory2,
  TbPlant2,
  TbShoppingCart,
  TbSchool,
  TbNews,
  TbSend,
  TbCircleCheck,
  TbHeart,
  TbExternalLink,
  TbTruck,
  TbSparkles,
  TbHome,
} from "react-icons/tb";

/* ─── Color constants ─── */
const C = {
  red: "#DA2128",
  redAlt: "#DB2628",
  cream: "#FAF7F2",
  white: "#FFFFFF",
  lightCream: "#FEF7F4",
  orange: "#DB6400",
  gold: "#F59C00",
  dark: "#1A1A2E",
  darkMuted: "#333355",
  gray: "#6B7280",
  lightGray: "#E5E7EB",
};

/* ─── Section wrapper ─── */
function Section({ children, className = "", id = "", style }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={className}
      style={style}
    >
      {children}
    </motion.section>
  );
}

/* ─── Counter ─── */
function Counter({ end, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = end / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Particles ─── */
function Particles({ count = 14, color = "white" }) {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 7,
    size: 10 + Math.random() * 18,
    opacity: 0.08 + Math.random() * 0.12,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, opacity: p.opacity, fontSize: p.size, color }}
          animate={{ y: [20, -80, 20], rotate: [0, 180, 360] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "linear" }}
        >
          <TbLeaf />
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Breadcrumb ─── */
function Breadcrumb() {
  return (
    <nav
      aria-label="Breadcrumb"
      className="absolute left-4 top-4 z-20 sm:left-8 sm:top-6"
    >
      <ol className="flex flex-wrap items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-xs">
        <li>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]"
          >
            <TbHome className="h-3.5 w-3.5" strokeWidth={2.2} />
            Home
          </a>
        </li>
        <li className="text-black/30">›</li>
        <li>
          <a href="#sectors" className="transition-colors duration-300 hover:text-[#DA2128]">
            Sectors
          </a>
        </li>
        <li className="text-black/30">›</li>
        <li>
          <a href="#agriculture" className="transition-colors duration-300 hover:text-[#DA2128]">
            Agriculture & Food Processing
          </a>
        </li>
        <li className="text-black/30">›</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm normal-case">
          Food Processing & Value Addition
        </li>
      </ol>
    </nav>
  );
}

/* ─── Section Header (Editorial Style) ─── */
function SectionHeader({
  badge,
  badgeColor = C.red,
  title,
  italicAccent,
  description,
  align = "center",
}) {
  return (
    <div className={`mb-10 ${align === "center" ? "text-center" : "text-left"}`}>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className={`mb-3 inline-flex items-center gap-2 ${align === "center" ? "mx-auto" : ""}`}
      >
        <div className="h-[2px] w-6 rounded-full" style={{ backgroundColor: badgeColor }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: badgeColor }}>
          {badge}
        </span>
        <div className="h-[2px] w-6 rounded-full" style={{ backgroundColor: badgeColor }} />
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        viewport={{ once: true }}
        className="font-fraunces text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-[42px]"
        style={{ color: C.dark }}
      >
        {title}
        {italicAccent && (
          <>
            {" "}
            <span
              className="font-fraunces-italic font-medium"
              style={{
                background: `linear-gradient(135deg, ${C.red}, ${C.gold})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {italicAccent}
            </span>
          </>
        )}
      </motion.h2>
      {description && (
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className={`mt-4 text-sm leading-relaxed text-gray-500 ${align === "center" ? "mx-auto max-w-2xl" : ""}`}
        >
          {description}
        </motion.p>
      )}
    </div>
  );
}

/* ─── Hero (Compact) ─── */
function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 400], [0, 100]);

  return (
    <section className="relative flex min-h-[72vh] items-center overflow-hidden sm:min-h-[68vh] lg:min-h-[70vh]">
      <motion.div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${C.dark} 0%, #2D1B1B 35%, ${C.red} 75%, ${C.orange} 100%)`,
          y,
        }}
      />
      <div className="grain absolute inset-0" />
      <div className="absolute -right-32 top-10 h-96 w-96 rounded-full opacity-30 blob" style={{ background: `radial-gradient(circle, ${C.gold}, transparent 70%)` }} />
      <div className="absolute -left-20 bottom-10 h-80 w-80 rounded-full opacity-20 blob" style={{ background: `radial-gradient(circle, ${C.red}, transparent 70%)` }} />
      <Particles count={10} />

      <Breadcrumb />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-20 sm:px-6 sm:pt-24">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md"
            >
              <span className="flex h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[11px] font-semibold text-white/90">MoFPI · Government of India</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="font-fraunces mb-4 text-4xl font-bold leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-[68px]"
            >
              Food
              <br />
              <span className="font-fraunces-italic font-normal shimmer-text">Processing</span>
              <br />
              <span className="text-2xl font-light sm:text-3xl lg:text-4xl" style={{ color: "rgba(255,255,255,0.75)" }}>
                & Value <span className="shimmer-text font-medium">Addition</span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="font-fraunces-italic mb-6 max-w-xl text-sm font-light leading-relaxed text-white/80 sm:text-base"
            >
              India's food ecosystem offers huge opportunities with stimulating growth, favorable economic policies, and attractive fiscal incentives — backed by MoFPI.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="flex flex-wrap gap-3"
            >
              <motion.a
                href="#overview"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="group rounded-full px-6 py-3 text-xs font-bold text-white shadow-xl glow-pulse"
                style={{ backgroundColor: C.red }}
              >
                Explore Council <TbArrowRight className="ml-1 inline h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </motion.a>
              <motion.a
                href="#query"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="rounded-full border-2 border-white/30 px-6 py-3 text-xs font-bold text-white backdrop-blur-sm"
              >
                <TbPlayerPlay className="mr-1.5 inline h-3.5 w-3.5" />
                Raise a Query
              </motion.a>
            </motion.div>
          </div>

          {/* Hero Cards */}
          <div className="hidden relative h-[380px] lg:block">
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="float-medium absolute right-0 top-2 w-60 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-xl"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: C.gold + "33" }}>
                  <TbChartBar className="h-4 w-4" style={{ color: C.gold }} strokeWidth={2.2} />
                </div>
                <span className="text-[11px] font-medium text-white/70">Food Grocery CAGR</span>
              </div>
              <div className="text-3xl font-extrabold text-white"><Counter end={25} suffix="%" /></div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${C.gold}, ${C.orange})` }} initial={{ width: 0 }} animate={{ width: "75%" }} transition={{ duration: 2, delay: 1 }} />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="float-slow absolute left-0 top-36 w-52 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-xl"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: C.red + "33" }}>
                  <TbBuildingFactory2 className="h-4 w-4" style={{ color: C.red }} strokeWidth={2.2} />
                </div>
                <span className="text-[11px] font-medium text-white/70">Mega Food Parks</span>
              </div>
              <div className="text-3xl font-extrabold text-white"><Counter end={42} /></div>
              <div className="mt-2 flex gap-1">
                {Array.from({ length: 10 }, (_, i) => (
                  <motion.div key={i} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: i < 4 ? C.red : "rgba(255,255,255,0.15)" }} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.3, delay: 1.3 + i * 0.08 }} />
                ))}
              </div>
              <div className="mt-2 text-[10px] text-white/50">17 Functional</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="float-medium absolute bottom-2 right-8 w-48 rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl"
            >
              <div className="mb-2 flex items-center gap-2">
                <TbShoppingCart className="h-3.5 w-3.5" style={{ color: C.gold }} strokeWidth={2.2} />
                <span className="text-[10px] font-medium text-white/70">Food Services</span>
              </div>
              <div className="text-2xl font-extrabold text-white"><Counter end={15} suffix="%" /></div>
              <div className="text-[10px] text-white/50">CAGR Growth Rate</div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2" animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
        <div className="flex h-8 w-5 items-start justify-center rounded-full border-2 border-white/30 pt-1.5">
          <motion.div className="h-1.5 w-0.5 rounded-full bg-white/60" animate={{ y: [0, 6, 0] }} transition={{ duration: 1.3, repeat: Infinity }} />
        </div>
      </motion.div>
    </section>
  );
}

/* ─── Overview ─── */
function Overview() {
  const stats = [
    { icon: <TbBuildingFactory2 strokeWidth={2.2} />, value: 42, suffix: "", label: "Mega Food Parks" },
    { icon: <TbPlant2 strokeWidth={2.2} />, value: 17, suffix: "", label: "Operational" },
    { icon: <TbTrendingUp strokeWidth={2.2} />, value: 25, suffix: "%", label: "Grocery CAGR" },
    { icon: <TbShoppingCart strokeWidth={2.2} />, value: 15, suffix: "%", label: "Services CAGR" },
  ];

  return (
    <Section id="overview" className="py-14" style={{ backgroundColor: C.cream }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Overview"
          badgeColor={C.red}
          title="India's Food Processing"
          italicAccent="Landscape"
        />

        <div className="mb-8 grid gap-5 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-md hover:shadow-xl transition-shadow">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${C.red}, transparent)` }} />
            <div className="relative">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `linear-gradient(135deg, ${C.red}20, ${C.orange}20)` }}>
                <TbWorld className="h-5 w-5" style={{ color: C.red }} strokeWidth={2.2} />
              </div>
              <h3 className="font-fraunces mb-2 text-xl font-bold" style={{ color: C.dark }}>A Growing <span className="font-fraunces-italic" style={{ color: C.red }}>Ecosystem</span></h3>
              <p className="text-sm leading-relaxed text-gray-600">
                India's food ecosystem offers <em className="font-fraunces-italic font-medium" style={{ color: C.red }}>huge opportunities</em> for investments with stimulating growth in food retail, favorable economic policies, and attractive fiscal incentives. The government has sanctioned <strong className="font-bold" style={{ color: C.dark }}>42 Mega Food Parks</strong> (17 operational) under MoFPI. Food Grocery at <strong className="font-bold" style={{ color: C.orange }}>25% CAGR</strong> and Food Services at <strong className="font-bold" style={{ color: C.orange }}>15% CAGR</strong>.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-md hover:shadow-xl transition-shadow">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${C.gold}, transparent)` }} />
            <div className="relative">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `linear-gradient(135deg, ${C.orange}20, ${C.gold}20)` }}>
                <TbBuilding className="h-5 w-5" style={{ color: C.orange }} strokeWidth={2.2} />
              </div>
              <h3 className="font-fraunces mb-2 text-xl font-bold" style={{ color: C.dark }}>ASSOCHAM's <span className="font-fraunces-italic" style={{ color: C.orange }}>Role</span></h3>
              <ul className="space-y-2">
                {[
                  "Bridges Government and Industries gap",
                  "Policy advocacy & representation",
                  "Outreach of Government schemes",
                  "Awareness for last mile delivery",
                ].map((item, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: 15 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08 }} viewport={{ once: true }} className="flex items-start gap-2 text-sm text-gray-600">
                    <TbCircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: C.red }} strokeWidth={2.2} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="group relative overflow-hidden rounded-xl bg-white p-4 text-center shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r" style={{ backgroundImage: `linear-gradient(to right, ${C.red}, ${C.gold})` }} />
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg transition-transform group-hover:rotate-12" style={{ backgroundColor: C.red + "12", color: C.red }}>
                {s.icon}
              </div>
              <div className="text-2xl font-extrabold" style={{ color: C.red }}>
                <Counter end={s.value} suffix={s.suffix} />
              </div>
              <div className="text-[11px] font-medium text-gray-500">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Leadership ─── */
function Leadership() {
  const leaders = [
    { name: "D.S. Rajora", role: "Chairman, Council on Food Processing", bio: "Leading the council's strategic vision.", icon: <TbUsers strokeWidth={2.2} /> },
    { name: "Advisory Board", role: "Industry Veterans & Policymakers", bio: "Experienced leaders across domains.", icon: <TbAward strokeWidth={2.2} /> },
    { name: "Executive Committee", role: "Operational Leadership", bio: "Day-to-day operations & engagement.", icon: <TbTarget strokeWidth={2.2} /> },
  ];

  return (
    <Section id="leadership" className="py-14" style={{ backgroundColor: C.white }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Leadership"
          badgeColor={C.orange}
          title="Guiding India's Food"
          italicAccent="Future"
        />

        <div className="grid gap-4 md:grid-cols-3">
          {leaders.map((l, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12 }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-2xl bg-white p-5 shadow-md hover:shadow-xl transition-shadow"
              style={{ border: `1px solid ${C.lightGray}` }}
            >
              <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(to right, ${C.red}, ${C.gold})` }} />
              <div className="flex items-center gap-4">
                <div
                  className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110 group-hover:rotate-6"
                  style={{ backgroundColor: i === 0 ? C.red + "15" : i === 1 ? C.orange + "15" : C.gold + "15" }}
                >
                  <span style={{ color: i === 0 ? C.red : i === 1 ? C.orange : C.gold }} className="h-6 w-6">{l.icon}</span>
                </div>
                <div>
                  <h3 className="font-fraunces text-base font-bold leading-tight" style={{ color: C.dark }}>{l.name}</h3>
                  <p className="text-[11px] font-semibold" style={{ color: C.red }}>{l.role}</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-gray-500">{l.bio}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Core Functions ─── */
function CoreFunctions() {
  const functions = [
    { icon: <TbShield strokeWidth={2.2} />, title: "Policy Advocacy", desc: "Influence food processing policies before government." },
    { icon: <TbHandClick strokeWidth={2.2} />, title: "Govt-Industry Bridge", desc: "Facilitate dialogue between policymakers & industry." },
    { icon: <TbBulb strokeWidth={2.2} />, title: "Innovation Promotion", desc: "Drive tech adoption in food processing." },
    { icon: <TbWorld strokeWidth={2.2} />, title: "Market Access", desc: "Open domestic & international markets." },
    { icon: <TbSchool strokeWidth={2.2} />, title: "Capacity Building", desc: "Workshops, seminars & training programs." },
    { icon: <TbFileText strokeWidth={2.2} />, title: "Research & Reports", desc: "Policy briefs & market intelligence." },
    { icon: <TbStar strokeWidth={2.2} />, title: "Best Practices", desc: "Global standards in food safety & quality." },
    { icon: <TbHeart strokeWidth={2.2} />, title: "Farmer Empowerment", desc: "Connect farmers with processing units." },
  ];

  return (
    <Section id="functions" className="py-14" style={{ backgroundColor: C.cream }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="What We Do"
          badgeColor={C.red}
          title="Core Functions of the"
          italicAccent="Council"
          description="Driving the food processing agenda through strategic interventions, policy advocacy, and stakeholder collaboration."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {functions.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-xl bg-white p-4 shadow-sm hover:shadow-lg transition-all"
            >
              <div className="absolute inset-x-0 bottom-0 h-0.5 w-0 bg-gradient-to-r transition-all duration-500 group-hover:w-full" style={{ backgroundImage: `linear-gradient(to right, ${C.red}, ${C.gold})` }} />
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-110 group-hover:rotate-6" style={{ backgroundColor: C.red + "10", color: C.red }}>
                  <span className="h-4 w-4">{f.icon}</span>
                </div>
                <div>
                  <h3 className="font-fraunces text-sm font-bold leading-tight" style={{ color: C.dark }}>{f.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-gray-500">{f.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Spotlight ─── */
function Spotlight() {
  const highlights = [
    { title: "Mega Food Parks", desc: "42 sanctioned parks across India, 17 now operational.", icon: <TbBuildingFactory2 strokeWidth={2.2} />, color: C.red, stat: "42", statLabel: "Parks" },
    { title: "Digital Transformation", desc: "Industry 4.0 in food processing supply chains.", icon: <TbTrendingUp strokeWidth={2.2} />, color: C.orange, stat: "25%", statLabel: "Growth" },
    { title: "Sustainable Practices", desc: "Environmentally responsible processing methods.", icon: <TbLeaf strokeWidth={2.2} />, color: C.gold, stat: "100+", statLabel: "Initiatives" },
  ];

  return (
    <Section className="py-14 relative overflow-hidden" style={{ backgroundColor: C.white }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Spotlight"
          badgeColor={C.orange}
          title="Key Focus"
          italicAccent="Areas"
        />

        <div className="grid gap-4 lg:grid-cols-3">
          {highlights.map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-2xl p-5 text-white shadow-lg"
              style={{ background: `linear-gradient(135deg, ${h.color}, ${h.color}dd)` }}
            >
              <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blob" />
              <div className="relative z-10">
                <div className="mb-4 flex items-start justify-between">
                  <div className="inline-flex rounded-xl bg-white/20 p-2.5 backdrop-blur-sm">{h.icon}</div>
                  <div className="text-right">
                    <div className="text-3xl font-extrabold leading-none">{h.stat}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/70">{h.statLabel}</div>
                  </div>
                </div>
                <h3 className="font-fraunces mb-1.5 text-lg font-bold">{h.title}</h3>
                <p className="text-xs leading-relaxed text-white/80">{h.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Stakeholders ─── */
function Stakeholders() {
  const stakeholders = [
    { icon: <TbPlant2 strokeWidth={2.2} />, label: "Farmers & FPOs" },
    { icon: <TbBuildingFactory2 strokeWidth={2.2} />, label: "Food Processors" },
    { icon: <TbShoppingCart strokeWidth={2.2} />, label: "Retail Chains" },
    { icon: <TbBuilding strokeWidth={2.2} />, label: "Government Bodies" },
    { icon: <TbSchool strokeWidth={2.2} />, label: "Research Institutes" },
    { icon: <TbTruck strokeWidth={2.2} />, label: "Supply Chain" },
    { icon: <TbHeart strokeWidth={2.2} />, label: "Consumer Groups" },
    { icon: <TbWorld strokeWidth={2.2} />, label: "Export Partners" },
  ];

  return (
    <Section className="py-14" style={{ backgroundColor: C.lightCream }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Key Stakeholders"
          badgeColor={C.red}
          title="Who We Work"
          italicAccent="With"
        />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {stakeholders.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.06, type: "spring" }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.08, y: -3 }}
              className="flex flex-col items-center gap-2 rounded-xl bg-white p-4 shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <motion.div
                className="flex h-11 w-11 items-center justify-center rounded-xl transition-colors"
                style={{ backgroundColor: C.red + "10", color: C.red }}
                whileHover={{ backgroundColor: C.red, color: "#fff" }}
              >
                {s.icon}
              </motion.div>
              <span className="text-center text-[11px] font-semibold leading-tight" style={{ color: C.dark }}>
                {s.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Events ─── */
function Events() {
  const events = [
    { date: "Jan 2025", title: "National Food Processing Summit", desc: "2000+ stakeholders across the food value chain.", type: "Conference" },
    { date: "Mar 2025", title: "Policy Dialogue on FDI", desc: "Liberalizing foreign investment in food retail.", type: "Policy" },
    { date: "May 2025", title: "Food Tech Innovation Conclave", desc: "Cutting-edge processing & packaging tech.", type: "Conclave" },
    { date: "Jul 2025", title: "Farm to Fork Workshop", desc: "Regional supply chain integration workshops.", type: "Workshop" },
  ];

  return (
    <Section id="events" className="py-14" style={{ backgroundColor: C.white }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Engagements"
          badgeColor={C.orange}
          title="Upcoming & Recent"
          italicAccent="Events"
        />

        <div className="grid gap-4 md:grid-cols-2">
          {events.map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              className="group flex gap-4 rounded-xl bg-white p-4 shadow-md hover:shadow-xl transition-shadow"
              style={{ border: `1px solid ${C.lightGray}` }}
            >
              <div className="flex flex-col items-center">
                <div className="flex h-14 w-14 flex-col items-center justify-center rounded-xl text-white shadow-md" style={{ background: `linear-gradient(135deg, ${i % 2 === 0 ? C.red : C.orange}, ${C.gold})` }}>
                  <TbCalendar className="h-4 w-4" strokeWidth={2.2} />
                </div>
                <span className="mt-1.5 text-[9px] font-bold uppercase tracking-wide text-gray-400">{e.type}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-[11px] font-semibold" style={{ color: C.red }}>{e.date}</span>
                <h3 className="font-fraunces mb-1 text-base font-bold leading-tight group-hover:text-red-600 transition-colors" style={{ color: C.dark }}>{e.title}</h3>
                <p className="text-xs leading-relaxed text-gray-500">{e.desc}</p>
                <div className="mt-1.5 flex items-center gap-1 text-xs font-semibold" style={{ color: C.red }}>
                  Learn More <TbChevronRight className="h-3 w-3" strokeWidth={2.5} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Knowledge Hub ─── */
function KnowledgeHub() {
  const resources = [
    { icon: <TbNews strokeWidth={2.2} />, title: "Industry Reports", desc: "Market analysis & outlook.", items: 24 },
    { icon: <TbBook strokeWidth={2.2} />, title: "Policy Briefs", desc: "Government policy analysis.", items: 18 },
    { icon: <TbChartBar strokeWidth={2.2} />, title: "Market Intelligence", desc: "Real-time data & insights.", items: 32 },
    { icon: <TbFileText strokeWidth={2.2} />, title: "Case Studies", desc: "Success stories & practices.", items: 15 },
  ];

  return (
    <Section id="knowledge" className="py-14" style={{ backgroundColor: C.cream }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          badge="Knowledge Hub"
          badgeColor={C.red}
          title="Resources &"
          italicAccent="Insights"
          description="Access comprehensive research, policy analysis, and industry intelligence to stay ahead in the food processing sector."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {resources.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, boxShadow: "0 15px 30px rgba(0,0,0,0.1)" }}
              className="group relative overflow-hidden rounded-xl bg-white p-4 shadow-md"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-transform group-hover:rotate-6 group-hover:scale-110" style={{ backgroundColor: C.red + "10", color: C.red }}>
                  <span className="h-4 w-4">{r.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-fraunces text-sm font-bold leading-tight" style={{ color: C.dark }}>{r.title}</h3>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">{r.desc}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] font-bold" style={{ color: C.gold }}>{r.items} Publications</span>
                    <TbExternalLink className="h-3 w-3 text-gray-400 transition-transform group-hover:translate-x-0.5" strokeWidth={2.2} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Raise Query ─── */
function RaiseQuery() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <Section id="query" className="py-14" style={{ backgroundColor: C.white }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <motion.div className="mb-3 inline-flex items-center gap-2">
              <div className="h-[2px] w-6 rounded-full" style={{ backgroundColor: C.red }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: C.red }}>
                Raise a Query
              </span>
            </motion.div>
            <h2 className="font-fraunces mb-3 text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl" style={{ color: C.dark }}>
              Have a <span className="font-fraunces-italic shimmer-text">Question?</span>
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-gray-600">
              Seeking information about government schemes, policy clarifications, or need assistance with your food processing venture? Our council is here to help.
            </p>

            <div className="space-y-3">
              {[
                { icon: <TbMessage strokeWidth={2.2} />, text: "Policy & Scheme Related Queries" },
                { icon: <TbHandClick strokeWidth={2.2} />, text: "Investment & Partnership Opportunities" },
                { icon: <TbFileText strokeWidth={2.2} />, text: "Regulatory & Compliance Support" },
                { icon: <TbBulb strokeWidth={2.2} />, text: "Innovation & Technology Queries" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-red-50/50 transition-colors"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: C.red + "10", color: C.red }}>
                    {item.icon}
                  </div>
                  <span className="text-xs font-medium text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl bg-white p-6 shadow-xl"
            style={{ border: `1px solid ${C.lightGray}` }}
          >
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="flex flex-col items-center justify-center py-10 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }} className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                    <TbCircleCheck className="h-8 w-8 text-green-500" strokeWidth={2.2} />
                  </motion.div>
                  <h3 className="font-fraunces mb-1 text-xl font-bold" style={{ color: C.dark }}>Query Submitted!</h3>
                  <p className="text-sm text-gray-500">Our team will respond within 48 hours.</p>
                </motion.div>
              ) : (
                <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleSubmit} className="space-y-3">
                  <h3 className="font-fraunces text-xl font-bold" style={{ color: C.dark }}>Submit Your <span className="font-fraunces-italic" style={{ color: C.red }}>Query</span></h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input type="text" placeholder="Full Name" required className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" />
                    <input type="email" placeholder="Email" required className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" />
                  </div>
                  <input type="text" placeholder="Organization" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" />
                  <select required className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100">
                    <option value="">Select a category</option>
                    <option>Policy & Schemes</option>
                    <option>Investment Opportunities</option>
                    <option>Regulatory Compliance</option>
                    <option>Technology & Innovation</option>
                    <option>Market Access</option>
                    <option>Other</option>
                  </select>
                  <textarea rows={3} placeholder="Describe your query..." required className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" />
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-xs font-bold text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${C.red}, ${C.gold})` }}>
                    <TbSend className="h-3.5 w-3.5" strokeWidth={2.2} />
                    Submit Query
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Join CTA ─── */
function JoinCTA() {
  return (
    <section className="relative overflow-hidden py-14">
      <div className="absolute inset-0 animated-gradient" style={{ background: `linear-gradient(135deg, ${C.dark}, ${C.red}, ${C.orange}, ${C.dark})`, backgroundSize: "300% 300%" }} />
      <div className="grain absolute inset-0" />
      <Particles count={10} />

      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
        <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-md">
            <TbPlant2 className="h-3.5 w-3.5" strokeWidth={2.2} />
            <span className="text-[11px] font-semibold text-white/90">Join the Movement</span>
          </div>

          <h2 className="font-fraunces mb-4 text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            Be Part of India's
            <br />
            <span className="font-fraunces-italic shimmer-text font-normal">Food Revolution</span>
          </h2>

          <p className="font-fraunces-italic mx-auto mb-7 max-w-2xl text-base font-light leading-relaxed text-white/85">
            Whether you are an agri-business, policymaker, researcher, startup, or farmer organization — join the council to contribute to India's journey toward <span className="not-italic font-semibold text-white">inclusive, sustainable, and technology-enabled agriculture</span>.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <motion.a href="#query" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="rounded-full px-8 py-3 text-xs font-bold text-white shadow-xl glow-pulse" style={{ backgroundColor: C.gold }}>
              Join the Council <TbArrowRight className="ml-1.5 inline h-3.5 w-3.5" strokeWidth={2.5} />
            </motion.a>
            <motion.a href="#query" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="rounded-full border-2 border-white/30 px-8 py-3 text-xs font-bold text-white backdrop-blur-sm">
              Learn More
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Main ─── */
export default function SectorDs2() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: C.white, fontFamily: "'Inter', sans-serif" }}>
      <Hero />
      <Overview />
      <Leadership />
      <CoreFunctions />
      <Spotlight />
      <Stakeholders />
      <Events />
      <KnowledgeHub />
      <RaiseQuery />
    
    </div>
  );
}
